import {
  attach,
  createEvent,
  restore,
  sample,
  combine,
  Store,
  Effect,
  createStore
} from 'effector'
import { ServerToClientCallMessage } from 'features/call/call-proto-generated'
import { CALL_URL } from 'constants/call'
import { callWsStateService } from './call-ws-state.service'
import { callAnalyticsService } from './call-analytics.service'

type State = {
  ws: WebSocket | null
  connected: boolean
  shouldReconnect: boolean
}

class CallWsService {
  private readonly doSaveWebsocket = createEvent<WebSocket | null>()
  readonly doOpenConnection = createEvent<void>()
  readonly doCloseConnection = createEvent<void>()
  readonly doReconnect = createEvent<void>()

  readonly wsOpened = createEvent()
  readonly wsMessage = createEvent<MessageEvent<unknown>>()
  readonly wsError = createEvent<Event>()
  readonly wsClosed = createEvent()

  readonly $store: Store<State>

  readonly sendMessageFx: Effect<Uint8Array, Uint8Array | null>

  readonly $shouldReconnect = createStore<boolean>(true)
  readonly setShouldReconnect = createEvent<boolean>()

  readonly subByTag = new Map<string, VoidFunction | undefined>()

  readonly dontSayWelcomeOnReconnectFeatureEnabled = false
  isSentAnyMessagesInThisHeap = false

  constructor() {
    const $ws = restore(this.doSaveWebsocket, null)
    const $connected = createStore<boolean>(false)
      .on(this.wsOpened, () => true)
      .on([this.wsClosed, this.wsError], () => false)

    this.$shouldReconnect.on(this.setShouldReconnect, (_, nSt) => nSt)

    this.$store = combine<State>({
      ws: $ws,
      connected: $connected,
      shouldReconnect: this.$shouldReconnect
    })

    this.sendMessageFx = attach({
      source: { ws: $ws },
      effect: async (
        { ws },
        message: Uint8Array
      ): Promise<Uint8Array | null> => {
        if (!ws || ws.readyState !== WebSocket.OPEN) return null

        if (this.dontSayWelcomeOnReconnectFeatureEnabled) {
          this.isSentAnyMessagesInThisHeap = true
        }
        ws.send(message)
        return message
      }
    })

    this.wsOpened.watch(() => {
      callWsStateService.doSetInitialConnectTime(Date.now())
    })

    this.wsMessage.watch((msg) => {
      if (msg.data instanceof ArrayBuffer) {
        const msgDecoded = ServerToClientCallMessage.decode(
          new Uint8Array(msg.data)
        )
        callWsStateService.gotWebSocketMessage(msgDecoded)
      }
    })

    this.wsError.watch((err: Event) => {
      callAnalyticsService.webSocketConnectinError(err)
    })

    const cleanupFx = attach({
      source: this.$store,
      effect: (state): void => {
        this.cleanupImpl(state)
      }
    })

    const createConnectionIfNotExistsFx = attach({
      source: this.$store,
      effect: this.createConnectionIfNotExists
    })

    this.doCloseConnection.watch(() => {
      void cleanupFx()
    })

    this.doOpenConnection.watch(() => {
      void createConnectionIfNotExistsFx()
    })

    sample({
      clock: [this.doCloseConnection, this.wsClosed],
      target: attach({
        source: this.$store,
        effect: (state): void => {
          this.reconnectIfNeeded(state)
        }
      })
    })

    sample({
      clock: this.doReconnect,
      source: this.$shouldReconnect,
      fn: Boolean,
      target: [this.doCloseConnection, this.doOpenConnection]
    })
  }

  private handleWSOpen = (): void => {
    this.wsOpened()
  }

  private handleWSError = (err: Event): void => {
    console.log('ws error')
    this.wsError(err)
  }

  private handleWSMessage = async (event: MessageEvent<unknown>) => {
    this.wsMessage(event)
  }

  private handleWsClose = () => {
    console.log('ws closed')
    this.wsClosed()
  }

  private createConnectionIfNotExists = ({ ws }: State): void => {
    if (ws && ws.readyState !== ws.CLOSED) return

    if (window.isDevMode) {
      console.log('create connection')
    }

    const newWs = new WebSocket(CALL_URL)
    newWs.binaryType = 'arraybuffer'

    newWs.addEventListener('open', this.handleWSOpen)
    newWs.addEventListener('error', this.handleWSError)
    newWs.addEventListener('message', this.handleWSMessage)
    newWs.addEventListener('close', this.handleWsClose)

    this.doSaveWebsocket(newWs)
  }

  private cleanupImpl = ({ ws }: State): void => {
    if (window.isDevMode) {
      console.log('cleanup impl')
    }

    if (ws && ws.readyState <= ws.OPEN) {
      try {
        ws.close()
      } catch {
        // noop
      }
    }

    if (ws) {
      ws.removeEventListener('close', this.handleWsClose)
      ws.removeEventListener('message', this.handleWSMessage)
      ws.removeEventListener('error', this.handleWSError)
      ws.removeEventListener('open', this.handleWSOpen)

      this.doSaveWebsocket(null)
    }
  }

  private reconnectIfNeeded = ({ shouldReconnect }: State) => {
    if (!shouldReconnect) return

    window.setTimeout(() => {
      this.doOpenConnection()
    }, 3000)
  }

  sendRaw = (message: Uint8Array): void => {
    void this.sendMessageFx(message)
  }

  autoSubscribeToWsMsgOnOpen = (
    tag: string,
    callback: (msg: ServerToClientCallMessage) => void
  ): VoidFunction => {
    if (this.subByTag.has('tag')) {
      throw new Error(`tag ${tag}: multiple ws subscriptions - not allowed`)
    }

    const unsubscribeTag = (): void => {
      const unsubscribeTag = this.subByTag.get(tag)
      unsubscribeTag?.()
    }

    unsubscribeTag()
    const tagSub = callWsStateService.gotWebSocketMessage.watch(callback)

    this.subByTag.set(tag, () => tagSub.unsubscribe())

    return () => {
      unsubscribeTag()
    }
  }
}

export const callWsService = new CallWsService()
